import type { WhatsAppConfig } from '../hooks/useWhatsAppConfig';

/** NiHulKav doc_type states used in sendMsgAll */
export const DOC_CARTESET = 9;
export const DOC_SUPPLIER_CARTESET = 22;
export const DOC_OPEN_INVOICES = 21;

export function isMetaWaTemplateServer(waConfig: WhatsAppConfig | null | undefined): boolean {
    const server = Number(waConfig?.server);
    return server === 11 || server === 4 || server === 12;
}

export function isWaPatternMsg(msg: string): boolean {
    return msg.includes('#pattern=');
}

/** First line `#pattern=…` value, or null when [msg] is not a pattern block. */
export function parseWaPatternName(msg: string): string | null {
    if (!isWaPatternMsg(msg)) return null;
    const firstLine = msg.trim().split('\n')[0]?.trim() ?? '';
    const eq = firstLine.indexOf('=');
    if (eq <= 0 || firstLine.slice(0, eq) !== '#pattern') return null;
    const name = firstLine.slice(eq + 1).trim();
    return name || null;
}

/** Body line `key=value` from a `#pattern` block (e.g. `b=5` on monthly_invoice). */
export function parseWaPatternBodyValue(msg: string, key: string): string | null {
    for (const line of msg.split('\n')) {
        const idx = line.indexOf('=');
        if (idx <= 0) continue;
        if (line.slice(0, idx).trim() === key) {
            const v = line.slice(idx + 1).trim();
            return v || null;
        }
    }
    return null;
}

const WA_IMAGE_LINE = /^@(?:image|header_image)(?:=.*)?$/i;

export function parseWaImageHeaderUrl(msg: string): string | null {
    for (const line of String(msg || '').split('\n')) {
        const t = line.trim();
        if (t.startsWith('@image=') || t.startsWith('@header_image=')) {
            const url = t.slice(t.indexOf('=') + 1).trim();
            return url || null;
        }
    }
    return null;
}

export function waImageHeaderNeedsUrl(msg: string): boolean {
    if (parseWaPatternName(msg) === 'image_send' && !parseWaImageHeaderUrl(msg)) return true;
    const hasFlag = String(msg || '').split('\n').some((line) => WA_IMAGE_LINE.test(line.trim()));
    if (!hasFlag) return false;
    return !parseWaImageHeaderUrl(msg);
}

/** Insert or replace `@image=https://…` in a `#pattern` block (or wrap `image_send`). */
export function upsertWaImageHeader(msg: string, url: string): string {
    const safeUrl = String(url || '').trim().replace(/[\r\n]/g, '');
    if (!safeUrl) return msg;
    const line = `@image=${safeUrl}`;
    const raw = String(msg || '');
    const lines = raw.split('\n');
    const idx = lines.findIndex((l) => WA_IMAGE_LINE.test(l.trim()));
    if (idx >= 0) {
        lines[idx] = line;
        return lines.join('\n');
    }
    if (isWaPatternMsg(raw)) {
        let insertAt = 1;
        while (insertAt < lines.length) {
            const t = lines[insertAt].trim();
            if (t.startsWith('#') || t.startsWith('@')) insertAt += 1;
            else break;
        }
        lines.splice(insertAt, 0, line);
        return lines.join('\n');
    }
    return buildImageSendWaPattern(safeUrl);
}

export function clearWaImageHeaderUrl(msg: string): string {
    const lines = String(msg || '').split('\n');
    const idx = lines.findIndex((l) => WA_IMAGE_LINE.test(l.trim()));
    if (idx < 0) return msg;
    const t = lines[idx].trim();
    if (t === '@image' || t === '@header_image') return msg;
    lines[idx] = t.startsWith('@header_image') ? '@header_image' : '@image';
    return lines.join('\n');
}

/** Plain Baileys / unofficial WA caption for monthly invoices — backend replaces `#name`. */
export function buildPlainMonthlyInvoiceWaMessage(monthLabel: string): string {
    const label = monthLabel.trim();
    if (!label) {
        return 'היי #name\nמצורפת חשבונית';
    }
    const isMulti = label.includes('-') || label.includes(',');
    if (isMulti) {
        return `היי #name\nמצורפת חשבונית עבור חודשים ${label}`;
    }
    return `היי #name\nמצורפת חשבונית עבור חודש ${label}`;
}

/** Plain Baileys caption for generic document sends. */
export function buildPlainDocumentSendWaMessage(): string {
    return 'היי #name\nמצורף מסמך';
}

function buildPlainCartesetDebtWaMessage(): string {
    return 'היי #name\nמצורפת כרטסת חוב';
}

function buildPlainDebtReminderWaMessage(withDocument: boolean): string {
    if (withDocument) {
        return 'היי #name\nפתוח לך חוב של #var ₪ — מצורפות חשבוניות פתוחות';
    }
    return 'היי #name\nפתוח לך חוב של #var ₪';
}

export function invoiceMonthLabelForWa(
    fromMonth: number,
    fromYear: number,
    toMonth: number,
    toYear: number,
): string {
    const safeFromMonth = Math.max(1, Math.min(12, fromMonth || 1));
    const safeToMonth = Math.max(1, Math.min(12, toMonth || safeFromMonth));

    if (safeFromMonth === safeToMonth && fromYear === toYear) {
        return String(safeFromMonth);
    }

    const monthNums: number[] = [];
    let m = safeFromMonth;
    let y = fromYear;
    let safety = 0;
    while (safety < 24) {
        monthNums.push(m);
        if (m === safeToMonth && y === toYear) break;
        m += 1;
        if (m > 12) { m = 1; y += 1; }
        safety += 1;
    }

    if (monthNums.length <= 1) {
        return String(safeFromMonth);
    }

    return monthNums.join(', ');
}

export function buildMonthlyInvoiceWaPattern(monthLabel: string): string {
    return [
        '#pattern=monthly_invoice',
        '#category=UTILITY',
        '#language=he',
        '@header_document',
        'a=#name',
        `b=${monthLabel}`,
    ].join('\n');
}

export function buildCartesetDebtWaPattern(): string {
    return [
        '#pattern=carteset_debt',
        '#category=UTILITY',
        '#language=he',
        '@header_document',
        'a=#name',
    ].join('\n');
}

/** {{1}}=name. Amount in Meta is currently static text `#var`, not {{2}}. */
export function buildDebtReminderWaPattern(withDocument = false): string {
    return [
        '#pattern=debt_reminder',
        '#category=UTILITY',
        '#language=he',
        '@header_document',
        'a=#name',
    ].join('\n');
}

export function buildDocumentSendWaPattern(): string {
    return [
        '#pattern=document_send',
        '#category=UTILITY',
        '#language=he',
        '@header_document',
        'x=#name',
        'y=#doctype_he',
        'z=#docid',
    ].join('\n');
}

export function buildImageSendWaPattern(imageUrl?: string): string {
    const url = String(imageUrl || '').trim();
    return [
        '#pattern=image_send',
        '#category=UTILITY',
        '#language=he',
        url ? `@image=${url}` : '@image',
        'a=#name',
    ].join('\n');
}

/** PDF header + pay button. `@payment=#var` is the amount; the jar fills the URL button like `@link`. */
export function buildDocWithPaymentWaPattern(): string {
    return [
        '#pattern=doc_with_payment',
        '#category=UTILITY',
        '#language=he',
        '@header_document',
        '@payment=#var',
        'x=#name',
        'y=#doctype_he',
        'z=#docid',
    ].join('\n');
}

export function buildInvoicePaymentWaPattern(): string {
    return [
        '#pattern=invoice_payment',
        '#category=UTILITY',
        '#language=he',
        '@header_document',
        'a=#name',
    ].join('\n');
}

export function buildOrderManagmentWaPattern(): string {
    return [
        '#pattern=order_managment',
        '#category=UTILITY',
        '#language=he',
        '@link',
    ].join('\n');
}

export function buildOrderManagment2WaPattern(): string {
    return [
        '#pattern=order_managment2',
        '#category=UTILITY',
        '#language=he',
        '@link',
        'a=#name',
        'b=#date',
    ].join('\n');
}

export function buildHolidayWaPattern(): string {
    return [
        '#pattern=holiday',
        '#category=MARKETING',
        '#language=he',
        '@link',
        'a=#name',
        'b=#date',
    ].join('\n');
}

export function buildReminderWaPattern(withLink = true): string {
    return [
        '#pattern=reminder',
        '#category=UTILITY',
        '#language=he',
        ...(withLink ? ['@link'] : []),
        'a=#name',
    ].join('\n');
}

function positionalIndexes(text: string): number[] {
    const indexes = new Set<number>();
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(String(text || ''))) !== null) {
        indexes.add(Number(match[1]));
    }
    return Array.from(indexes).sort((a, b) => a - b);
}

export function waPositionalIndexes(text: string): number[] {
    return positionalIndexes(text);
}

export function sanitizeHubTemplateName(raw: string): string {
    return String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 512);
}

export function uniqueHubTemplateName(base: string, existing: string[]): string {
    const clean = sanitizeHubTemplateName(base) || 'template';
    const names = new Set(existing.map((name) => sanitizeHubTemplateName(name)));
    if (!names.has(clean)) return clean;
    for (let i = 2; i < 80; i += 1) {
        const next = `${clean}_${i}`.slice(0, 512);
        if (!names.has(next)) return next;
    }
    return `${clean}_${Date.now()}`.slice(0, 512);
}

export type HubTemplateButtonKind = 'none' | 'shop' | 'pay' | 'custom';

export type HubTemplateCreateDraft = {
    name: string;
    language?: string;
    category?: string;
    bodyText: string;
    headerFormat?: string;
    buttonKind?: HubTemplateButtonKind;
    customButtonUrl?: string;
    varRoles?: string[];
};

const BODY_VAR_KEYS = 'abcdefghijklmnopqrstuvwxyz';

function guessHubBodyPlaceholder(index: number, bodyText: string): string {
    const marker = `{{${index}}}`;
    const at = bodyText.indexOf(marker);
    const line = at >= 0
        ? (bodyText.slice(0, at + marker.length).split('\n').pop() || '')
        : '';
    const around = line || bodyText;
    if (/תאריך|יום/.test(around) && !/היי|שם|לקוח/.test(around)) return '#date';
    if (/תעוד|מסמך|חשבונ/.test(around) && index > 1) {
        return index === 2 ? '#doctype_he' : '#docid';
    }
    if (index === 1 || /שם|היי|לקוח/.test(around)) return '#name';
    if (index === 2) return '#date';
    if (index === 3) return '#docid';
    return '#var';
}

/** Wizard preview only. Send-message dumps the Hub API `pattern` for this inbox. */
export function buildWaPatternFromCreateDraft(draft: HubTemplateCreateDraft): string {
    const name = sanitizeHubTemplateName(draft.name);
    if (!name) return '';
    const language = String(draft.language || 'he').trim() || 'he';
    const category = String(draft.category || 'UTILITY').trim().toUpperCase() || 'UTILITY';
    const headerFormat = String(draft.headerFormat || 'NONE').toUpperCase();
    const lines = [
        `#pattern=${name}`,
        `#category=${category}`,
        `#language=${language}`,
    ];
    if (headerFormat === 'DOCUMENT') lines.push('@header_document');
    if (headerFormat === 'IMAGE') lines.push('@image');
    if (draft.buttonKind === 'pay') {
        lines.push('@payment=#var');
    } else if (draft.buttonKind === 'shop') {
        lines.push('@link');
    } else if (draft.buttonKind === 'custom') {
        const url = String(draft.customButtonUrl || '');
        if (/\{\{\s*1\s*\}\}/.test(url)) {
            if (/\/pay\/s\//i.test(url) || /nihulkav\.online\/pay/i.test(url)) {
                lines.push('@payment=#var');
            } else {
                lines.push('@link');
            }
        }
    }
    const indexes = positionalIndexes(draft.bodyText);
    indexes.forEach((index, i) => {
        const key = BODY_VAR_KEYS[i] || `p${index}`;
        const role = String(draft.varRoles?.[index - 1] || '').trim()
            || guessHubBodyPlaceholder(index, draft.bodyText);
        lines.push(`${key}=${role}`);
    });
    return lines.join('\n');
}

/** Baileys / email caption. `@payment=#var` creates the share URL; `#var` stays the amount. */
export function buildPlainDocWithPaymentWaMessage(): string {
    return '@payment=#var\n#docs\n\nלתשלום החשבון\n#var';
}

function isOpenInvoicesDocType(docType: number | undefined): boolean {
    return docType === DOC_OPEN_INVOICES;
}

function isDebtReminderLegacyMsg(msg: string): boolean {
    const t = msg.trim();
    return t.includes('#var')
        || t.includes('פתוח לך חוב')
        || t.includes('חשבוניות פתוחות');
}

function isGenericDocumentMsg(msg: string): boolean {
    const t = msg.trim();
    return t === '#docs'
        || t.includes('#docs')
        || t === 'מסמכים'
        || t === 'חשבונית'
        || t.startsWith('היי חשבונית')
        || t.startsWith('חשבונית ');
}

function isDocWithPaymentMsg(msg: string): boolean {
    const t = msg.trim();
    if (parseWaPatternName(t) === 'doc_with_payment') return true;
    if (t.includes('@payment')) return true;
    const hasPayLink = t.includes('/pay/s/');
    return hasPayLink && t.includes('לתשלום');
}

/** Hub used to dump `@link=#var` for pay buttons. Prefer `@payment=#var` (amount); `#var` stays generic. */
export function ensurePaymentPlaceholderInWaMsg(msg: string): string {
    const t = String(msg || '');
    if (!t.trim()) return t;
    const lines = t.split('\n');
    const isPaymentFlag = (line: string) => {
        const s = line.trim();
        return s === '@payment' || s.startsWith('@payment=');
    };
    const hasPayment = lines.some(isPaymentFlag);
    const stripped = lines
        .filter((line) => line.trim() !== '@link=#var')
        .map((line) => (line.trim() === '@payment' ? '@payment=#var' : line));
    if (hasPayment) return stripped.join('\n');
    // Do not infer payment from generic #var usage. Only the known payment
    // template may migrate its old @link=#var control line.
    const payPattern = parseWaPatternName(t) === 'doc_with_payment';
    if (!payPattern) return t;
    const headerAt = stripped.findIndex((line) => {
        const s = line.trim();
        return s === '@header_document' || s === '@header_doc';
    });
    if (headerAt >= 0) stripped.splice(headerAt + 1, 0, '@payment=#var');
    else stripped.push('@payment=#var');
    return stripped.join('\n');
}

export type ResolveWaMsgOpts = {
    msg: string;
    docTypes?: number[];
    useMetaTemplates: boolean;
};

/**
 * Maps legacy free-text / placeholder messages to Meta-approved `#pattern=…` blocks.
 * Email sends should pass the original `msg`; use this only for WhatsApp.
 */
export function resolveWhatsappMsgForSend(opts: ResolveWaMsgOpts): string {
    const { msg, docTypes, useMetaTemplates } = opts;
    if (!useMetaTemplates || !msg.trim()) return msg;
    // Whatever `#pattern=…` is already in the message box is sent as-is
    // (carteset_debt, document_send, or any other approved template).
    if (isWaPatternMsg(msg)) return ensurePaymentPlaceholderInWaMsg(msg);

    const hasFiles = !!docTypes?.length;

    if (isDocWithPaymentMsg(msg)) {
        return hasFiles ? buildDocWithPaymentWaPattern() : msg;
    }

    if (hasFiles && docTypes!.every(isOpenInvoicesDocType)) {
        return buildDebtReminderWaPattern(true);
    }

    if (!hasFiles && isDebtReminderLegacyMsg(msg)) {
        return buildDebtReminderWaPattern(false);
    }

    if (hasFiles && isGenericDocumentMsg(msg)) {
        return buildDocumentSendWaPattern();
    }

    if (hasFiles) {
        return buildDocumentSendWaPattern();
    }

    return msg;
}

/**
 * Maps Meta `#pattern=…` blocks to readable Hebrew for Baileys / unofficial WhatsApp.
 * Email sends should not use this — only WhatsApp when [isMetaWaTemplateServer] is false.
 */
export function resolvePlainWhatsappMsgForSend(
    opts: ResolveWaMsgOpts & { fallback?: string },
): string {
    const { msg, docTypes, fallback = 'מסמכים מצורפים' } = opts;
    const patternName = parseWaPatternName(msg);

    if (patternName === 'monthly_invoice') {
        return buildPlainMonthlyInvoiceWaMessage(parseWaPatternBodyValue(msg, 'b') || '');
    }
    if (patternName === 'carteset_debt') {
        return buildPlainCartesetDebtWaMessage();
    }
    if (patternName === 'debt_reminder') {
        return buildPlainDebtReminderWaMessage(msg.includes('@header_document'));
    }
    if (patternName === 'document_send') {
        return buildPlainDocumentSendWaMessage();
    }
    if (patternName === 'doc_with_payment') {
        return buildPlainDocWithPaymentWaMessage();
    }

    const fb = String(fallback || '').trim();
    const safeFallback = fb && !isWaPatternMsg(fb) && !isGenericDocumentMsg(fb) ? fb : '';

    if (isDocWithPaymentMsg(msg)) {
        return buildPlainDocWithPaymentWaMessage();
    }

    if (isWaPatternMsg(msg)) {
        return safeFallback || buildPlainDocumentSendWaMessage();
    }

    if (isGenericDocumentMsg(msg)) {
        const monthMatch = msg.match(/חודש(?:ים)?\s*([\d,\s-]+)/);
        if (monthMatch) {
            return buildPlainMonthlyInvoiceWaMessage(monthMatch[1].trim());
        }
        return safeFallback || 'היי #name\nמצורפת חשבונית';
    }

    const t = msg.trim();
    if (!t) {
        return safeFallback || (docTypes?.length ? buildPlainDocumentSendWaMessage() : 'שלום');
    }

    return msg;
}
